import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Input from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import Grid from '@mui/material/Grid';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import API from '../api';

import InputUpload from './InputUpload';

import { updateRetailer } from '../actions/Retailer';
import { useRefreshToken } from '../common/hooks/useRefreshToken';
import Loader from './Loader';
import notify from './Notification/helper';
import ViewPage from '../layouts/ViewPage';
import { chooseInputComponent } from '../common/utils/components';
import { getTranslation } from '../common/utils/translate';
import { Tab, Tabs } from '@mui/material';
import LanguageSwitcher from './LanguageSwitcher';
import PropTypes from 'prop-types';
import { languages } from '../utils/translate';

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

function BrandSeoForm() {
  const retailer = useSelector(state => state.retailer);
  const dispatch = useDispatch();
  
  const [selectedLanguage, setSelectedLanguage] = useState(languages[0]);

  let defaultPayload = {};

  retailer?.translations?.forEach(translation => {
    defaultPayload[translation.languages_code] = {
      ...translation,
    };
  });

  console.log('defaultPayload', defaultPayload);

  const [translationsPayload, setTranslationsPayload] = useState(defaultPayload || {});

  const [englishTranslation, setEnglishTranslation] = useState(getTranslation(retailer, { key: 'languages_code', code: 'en-US' }));

  const [retailerPayload, setRetailerPayload] = useState(retailer);

  const [loading, setLoading] = useState(false);

  const handleTranslationInputChange = (languageCode, field, value) => {
    if (translationsPayload[languageCode] === undefined) {
      setTranslationsPayload({
        ...translationsPayload,
        [languageCode]: {
          [field]: value
        }
      });
    } else {
      setTranslationsPayload({
        ...translationsPayload,
        [languageCode]: {
          ...translationsPayload[languageCode],
          [field]: value
        }
      });
    }
  };

  const formSections = [
    {
      title: 'SEO Settings',
      translations: [
        {
          label: 'Name',
          name: 'retailer_name',
          description: 'The text to identify a brand.',
          type: 'text',
          value: translationsPayload,
          options: [],
          handleInputChange: (field, value, languageCode) => {
            handleTranslationInputChange(languageCode, field, value);
          }
        },
        {
          label: 'Title Tag',
          name: 'title',
          description: 'ADD TEXT TO THE TITLE TAG.  THE TITLE WILL BEGIN WITH  “CANADIAN BEEF INFORMATION GATEWAY"',
          type: 'text',
          value: translationsPayload,
          options: [],
          handleInputChange: (field, value, languageCode) => {
            handleTranslationInputChange(languageCode, field, value);
          }
        },
        {
          label: 'Description',
          name: 'description',
          description: 'ADD TEXT TO THE DESCRIPTION TAG.  THE TITLE WILL BEGIN WITH  “CANADIAN BEEF INFORMATION GATEWAY”',
          type: 'rich-text',
          value: translationsPayload,
          options: [],
          handleInputChange: (field, value, languageCode) => {
            handleTranslationInputChange(languageCode, field, value);
          }
        }
      ],
    },
  ]

  const makeUpdateRetailerRequest = async () => {
    try {
      setLoading(true);

      let payload = {
        ...retailerPayload,
      };

      if (englishTranslation?.id) {
        // build translation payload
        const translations = { create: [], update: [], delete: [] };
        translations.update.push(englishTranslation)

        // override retailerPayload.translations
        payload.translations = translations;
      }

      const updateRetailerResponse = await API.updateRetailer(payload);
      notify('success', 'Retailer updated');

      dispatch(updateRetailer(updateRetailerResponse));
    } catch (error) {
      console.log('error: ', error);
      notify('error', 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return <Loader />;
  }

  return (
    <Box 
      sx={{
        flexGrow: 1,
      }}
    >
      <Grid container>
        <Grid item xs={12} md={7}>
          {formSections.map((section) => (
            <Box key={section.title}>
              {
                section.title &&
                <Typography
                  className='form-section-title'
                  variant="h4"
                >
                  {section.title}
                </Typography>
              }

              <Box>
                <Stack
                  direction={'row'}
                  spacing={1}
                  alignItems={'center'}
                  sx={{
                    background: '#efeeee',
                    p: 0.5,
                  }}
                >
                  <Typography
                    component={'div'}
                    sx={{
                      flexGrow: 1,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      px: 1,
                    }}
                  >
                    Edit in <strong>{ selectedLanguage.name }</strong>
                  </Typography>

                  {
                    languages.map((languageOption) => (
                      <Box
                        key={languageOption.code}
                        sx={{
                          position: 'relative',
                          height: '32px',
                          width: '28px',
                          cursor: 'pointer',
                        }}
                        onClick={() => { setSelectedLanguage(languageOption); }}
                      >
                        <Stack
                          direction="column"
                          justifyContent="center"
                          alignItems="center"
                          className=''
                          sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            top: 0,
                            bottom: 0,
                            background: '#fff',
                            borderBottom: (
                              selectedLanguage.code === languageOption.code ?
                              `4px solid ${retailer.button_background_color || 'red'}` :
                              '4px solid #efeeee'
                            ),
                            borderTop: (
                              (!translationsPayload?.[languageOption.code]?.['name'] ? '2px solid #efeeee' : '2px solid green')
                            ),
                            borderRadius: '0'
                          }}
                        >
                          <Typography
                            sx={{
                              color: 'black',
                              fontSize: 16,
                              textTransform: 'uppercase',
                              fontWeight: selectedLanguage.code === languageOption.code ? 'bold' : 'normal',
          
                            }}
                          >
                            {languageOption.code.substring(0, 2)}
                          </Typography>
                        </Stack>
                      </Box>
                    ))
                  }
                </Stack>

                <Box>
                  {
                    languages.map((language) => (
                      <CustomTabPanel
                        key={language.name}
                        value={selectedLanguage}
                        index={language}
                      >
                        <Grid container spacing={2}>
                          {section?.translations?.map(field => (
                            <Fragment key={field.name}>
                              <Grid
                                key={field.name}
                                item
                                {
                                ...(['colour'].includes(field?.type) ? {
                                  xs: 12,
                                  md: 4,
                                } : {
                                  xs: 12,
                                }
                                )
                                }
                              >
                                {chooseInputComponent(field, language.code)}
                              </Grid>
                            </Fragment>
                          ))}
                        </Grid>
                      </CustomTabPanel>
                    ))
                  }
                </Box>
              </Box>
            </Box>
          ))}
        </Grid>

        <Grid item xs={12} md={7} >
          <Button
            disabled={loading}
            className='form-button'
            sx={{ width: '40%', my: 5 }}
            size="large"
            variant="contained"
            onClick={makeUpdateRetailerRequest}
          >
            {loading ? 'Updating... ' : 'Update' }
          </Button>
        </Grid>
      </Grid>
    </Box>
  )
}

export default BrandSeoForm